import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchMyRosterEntries,
  fetchMyTenures,
  fetchRosterEntries,
  fetchRosterEntry,
  fetchRosters,
  postDisconnectEntry,
  postFreezeEntry,
  postGiveUpEntry,
  postRosterApplication,
  postSelectEntry,
  postThawEntry,
  setEntryProfilePicture,
} from './api';
import type { RosterEntryFilters } from './api';
import type { RosterEntryData, RosterData } from './types';
import type { PaginatedResponse } from '@/shared/types';
import { useAccount } from '@/store/hooks';

export function useRosterEntryQuery(id: RosterEntryData['id']) {
  return useQuery({
    queryKey: ['roster-entry', id],
    queryFn: () => fetchRosterEntry(id),
    enabled: !!id,
    throwOnError: true,
  });
}

type MyRosterEntriesOptions = {
  enabled?: boolean;
  refetchInterval?: number;
  refetchIntervalInBackground?: boolean;
  refetchOnWindowFocus?: boolean;
};

/**
 * The account's own characters. Options are per observer in TanStack Query, so
 * the game screen can poll for #3774's attention counts without every other
 * consumer of this query key (useActiveCharacterId, useWorldBuilderActor,
 * Header) starting to poll too.
 */
export function useMyRosterEntriesQuery(options: boolean | MyRosterEntriesOptions = true) {
  const account = useAccount();
  const opts = typeof options === 'boolean' ? { enabled: options } : options;
  return useQuery({
    queryKey: ['my-roster-entries'],
    queryFn: fetchMyRosterEntries,
    throwOnError: true,
    ...opts,
    enabled: !!account && (opts.enabled ?? true),
  });
}

export function useMyTenuresQuery(enabled = true) {
  const account = useAccount();
  return useQuery({
    queryKey: ['my-tenures'],
    queryFn: fetchMyTenures,
    enabled: !!account && enabled,
    throwOnError: true,
  });
}

export function useRostersQuery() {
  return useQuery({
    queryKey: ['rosters'],
    queryFn: fetchRosters,
    throwOnError: true,
  });
}

export function useRosterEntriesQuery(
  rosterId: RosterData['id'] | undefined,
  page: number,
  filters: RosterEntryFilters
) {
  return useQuery<PaginatedResponse<RosterEntryData>>({
    queryKey: ['roster-entries', rosterId, page, filters],
    queryFn: () => fetchRosterEntries(rosterId!, page, filters),
    enabled: !!rosterId,
    throwOnError: true,
  });
}

/**
 * Public roster search by exact persona name (#2156) — the character-card drawer's
 * ONLY allowed identity-resolution path. Searches across every public roster (no
 * `roster` scope) since a persona name isn't tied to one; the `name` filter is
 * `icontains` server-side, so callers must still check for an exact
 * `result.character.name === name` match before treating it as a hit — a
 * disguised/temporary persona whose name doesn't exactly match a public roster
 * entry must render as "not on the roster," never fall back to a substring match.
 */
export function useRosterEntryByNameQuery(name: string | undefined) {
  return useQuery<PaginatedResponse<RosterEntryData>>({
    queryKey: ['roster-entry-by-name', name],
    queryFn: () => fetchRosterEntries(undefined, 1, { name }),
    enabled: !!name,
    throwOnError: true,
  });
}

/**
 * Wear one of the character's looks (#3898) — sets the roster entry's profile picture
 * to that image.
 *
 * Invalidates both the roster entry (whose `profile_picture` the roster and other
 * surfaces read) and the character sheet payload (whose `looks` carry the `is_current`
 * flag the strip marks), so the plate and the strip agree again without a reload.
 * `sheetId` is the CharacterSheet pk, which the sheet query is keyed on.
 */
export function useWearLook(entryId: RosterEntryData['id'], sheetId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenureMediaId: number) => setEntryProfilePicture(entryId, tenureMediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roster-entry', entryId] });
      queryClient.invalidateQueries({ queryKey: ['character-sheets', sheetId] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to change the look.'),
  });
}

export function useSendRosterApplication(id: RosterEntryData['id']) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => postRosterApplication(id, message),
    onSuccess: () => {
      toast.success('Application sent! Staff will review it; you will get an email.');
      queryClient.invalidateQueries({ queryKey: ['roster-entry', id] });
      // ['account'] is the query key useAccountQuery() (mounted globally via
      // AuthProvider) reads and mirrors into Redux — invalidating it here
      // refetches /api/user/ so the new pending_applications entry shows up
      // without a full page reload.
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to send.'),
  });
}

/**
 * Set (or clear) the account's durable server-side character selection
 * (#3412 state 2.5 substrate). Invalidates `['account']` on success — the
 * key `useAccountQuery()` (mounted globally via `AuthProvider`) reads; its
 * own success effect re-hydrates `gameSlice.active`/`activeEntryId` from the
 * refetched `selected_entry`, so this mutation never writes to Redux
 * directly. Fire-and-forget from callers: a failed select degrades to "the
 * local switch worked, but a reload might not restore it" rather than
 * blocking the (already-immediate) local session switch.
 */
export function useSelectCharacterMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number | null) => postSelectEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
    // PLACEHOLDER wording — matches useSendRosterApplication's toast pattern
    // above. The local switch already happened (fire-and-forget, see the doc
    // comment above), so this is purely "your reload-durable pick didn't
    // save" — never blocking, never re-thrown.
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't save your character selection."),
  });
}

/**
 * Disconnect a character everywhere (#4321). Refetches the roster list, which
 * carries the server's `session_count`, so the chip and the Hall card stop
 * saying "In the world" as soon as the sessions are gone.
 */
export function useDisconnectEverywhereMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) => postDisconnectEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-roster-entries'] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not disconnect that character.'),
  });
}

/**
 * Slot actions (#3996). Each invalidates the Hall's list and the account
 * payload, which carries the slot ledger.
 */
function useSlotActionMutation<T>(action: (entryId: number) => Promise<T>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-roster-entries'] });
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });
}

export function useFreezeEntryMutation() {
  return useSlotActionMutation(postFreezeEntry);
}

export function useThawEntryMutation() {
  return useSlotActionMutation(postThawEntry);
}

export function useGiveUpEntryMutation() {
  return useSlotActionMutation(postGiveUpEntry);
}
